import { equals } from '../lodash';
import { activateConsumer, Consumer, deactivateConsumer, trackDeps } from './deps';
import { prefix } from '../../lib/logger';

const { error } = prefix('reactive-value');

const rawTargetSymbol = Symbol('rawTarget');

export function isReactive<T>(val: unknown): val is ReactiveValue<T> {
  return val instanceof SignalValue || val instanceof ComputedValue;
}

export function subscribeAll<T>(
  reactiveValues: ReactiveValue<T>[],
  callback: (oldValue: T) => void
) {
  const unsubscribes = reactiveValues.map((rv) => rv.subscribe(callback));
  return () => {
    unsubscribes.forEach((unsub) => unsub());
  };
}

export interface ReactiveValue<T> {
  value: T;
  get(): T;
  subscribe: (callback: (oldValue: T) => void) => () => void;
  listeners: Set<Consumer>;

  dispose(): void;
}

export function createSignal<T>(
  initialVal: T,
  deep?: boolean,
  deepWatcher?: (path: (string | symbol)[], oldVal: unknown) => void
) {
  return new SignalValue(initialVal, deep, deepWatcher);
}

export function createComputed<T>(getter: () => T) {
  return new ComputedValue(getter);
}

export class SignalValue<T> implements ReactiveValue<T> {
  private _val: T;
  private _deep: boolean;
  private _deepWatcher: null | ((path: (string | symbol)[], oldVal: unknown) => void) = null;
  private _setWatchers: ((oldVal: T) => void)[] = [];

  readonly listeners: Set<Consumer> = new Set();

  get value(): T {
    return this.get();
  }

  get() {
    trackDeps(this);
    return this._val;
  }

  peek() {
    return this._val;
  }

  set(newVal: T) {
    if (this._val === newVal) {
      return this._val;
    }

    const oldVal = this.toObject();

    if (this._deep) {
      this._val = this._convertFieldToSignal(newVal);
    } else {
      this._val = newVal;
    }

    [...this._setWatchers].forEach((cb) => cb(oldVal));
    [...this.listeners].forEach((val) => val.markDirty());

    return this._val;
  }

  subscribe(callback: (value: T) => void) {
    this._setWatchers.push(callback);

    return () => {
      this.unsubscribe(callback);
    };
  }

  unsubscribe(callback: (value: T) => void) {
    this._setWatchers = this._setWatchers.filter((cb) => cb !== callback);
  }

  constructor(
    initialVal: T,
    deep: boolean = false,
    _deepWatcher: SignalValue<unknown>['_deepWatcher'] = null
  ) {
    this._deep = deep;
    this._deepWatcher = _deepWatcher;

    if (deep) {
      this._val = this._convertFieldToSignal(initialVal);
    } else {
      this._val = initialVal;
    }
  }

  toObject(): T {
    if (!this._deep) {
      return this._val;
    }

    return this._val instanceof SignalValue
      ? this._val.toObject()
      : JSON.parse(JSON.stringify(this._val));
  }

  private _notify(path: (string | symbol)[], oldValue: unknown) {
    this._deepWatcher?.(path, oldValue);
  }

  private _convertFieldToSignal(val: T) {
    if (typeof val !== 'object' || !val) {
      return val;
    }

    const notify = (path: (string | symbol)[], oldVal: unknown) => {
      this._notify(path, oldVal);
    };

    const toSignal = (prop: string | symbol, propValue: unknown) => {
      return createSignal(propValue, true, (path, oldVal) => {
        const curPath = [prop, ...path];

        notify(curPath, oldVal);
      });
    };

    Object.entries(val).forEach(([prop, fieldVal]) => {
      (val as any)[prop] = toSignal(prop, fieldVal);
    });

    return new Proxy(val, {
      set(target, prop, newValue, receiver) {
        let oldVal = Reflect.get(target, prop, receiver);
        oldVal = oldVal instanceof SignalValue ? oldVal.toObject() : oldVal;

        (target as any)[prop].set(newValue);

        notify([prop], oldVal);
        return true;
      },
      get(target, prop, receiver) {
        if (prop === rawTargetSymbol) {
          return target;
        }

        const val = Reflect.get(target, prop, receiver);

        if (val instanceof SignalValue) {
          return val.value;
        }

        return val;
      },
    });
  }

  dispose() {
    if (this._deep) {
      const val = this._val as any;
      const rawTarget = val[rawTargetSymbol];
      Object.keys(rawTarget).forEach((key) => {
        if (isReactive(rawTarget[key])) {
          rawTarget[key].dispose();
        }
      });
    }

    this._setWatchers = [];
    this.listeners.clear();
  }
}

export class ComputedValue<T> implements ReactiveValue<T>, Consumer {
  private _getter: () => T;
  private _dirty: boolean = true;
  private _computing: boolean = true;
  private _cacheVal: T | undefined = undefined;

  /**
   * Dependencies that this computed value relies on
   */
  readonly deps: Set<ReactiveValue<T>> = new Set();
  readonly listeners: Set<Consumer> = new Set();

  private _setWatchers: ((oldVal: T) => void)[] = [];

  get value(): T {
    return this.get();
  }

  get() {
    trackDeps(this);
    if (this._dirty) {
      const oldVal = this._cacheVal;
      if (this._computing) {
        error('Circular dependency detected');
        return this._cacheVal as T;
      }
      const newVal = this._rebuild();
      this._cacheVal = newVal;
      this._dirty = false;

      if (!equals(oldVal, newVal)) {
        this._setWatchers.forEach((cb) => cb(oldVal as T));
      }
    }

    return this._cacheVal as T;
  }

  peek() {
    return this._cacheVal as T;
  }

  private _rebuild() {
    this.deps.forEach((dep) => {
      dep.listeners.delete(this as Consumer);
    });
    this.deps.clear();
    activateConsumer(this as Consumer);
    this._cacheVal = this._getter();
    deactivateConsumer();

    return this._cacheVal;
  }

  subscribe(callback: (oldValue: T) => void): () => void {
    this._setWatchers.push(callback);

    return () => {
      this.unsubscribe(callback);
    };
  }

  unsubscribe(callback: (oldValue: T) => void) {
    this._setWatchers = this._setWatchers.filter((cb) => cb !== callback);
  }

  markDirty() {
    this._dirty = true;
    [...this.listeners].forEach((val) => val.markDirty());
  }

  constructor(getter: () => T) {
    this._getter = getter;
    this._rebuild();
  }

  dispose() {
    this.deps.forEach((dep) => {
      dep.listeners.delete(this as Consumer);
    });

    this.deps.clear();
    this.listeners.clear();
    this._setWatchers = [];
  }
}
